// Shared 4-way loading standard (M7 Chunk 3 / 2c, ASK-080 §E). The going-forward bar:
// match the indicator to the situation, show feedback within 300ms, never block the
// whole screen. Ratified rollout (Shaun 2026-06-23): module + high-impact surfaces now;
// 4-way is MANDATORY on every new/touched surface henceforth; remaining surfaces are a
// tracked burn-down by M7 close (Coordination owns the checklist + verify-all gates).
//
// The 4 ways (research-brief 2c):
//   skeleton   — content in a known shape (catalogue grid, product card, wizard step)
//   progress   — measurable work; STAGED w/ phase labels for the big 3D scene/teleport load
//   inline     — short blocking action ON an element (checkout button, capture)
//   optimistic — frequent low-risk toggle (add-to-cart, knob flips): apply now, revert on fail
//
// Pure helpers (plan/staged-fraction/300ms rule) are unit-tested; DOM adapters are
// guarded (no top-level `document`) so this imports cleanly in node.

export const SPINNER_DELAY_MS = 300;   // "never show nothing" — but don't flash for sub-300ms work

// Audit checklist → indicator type (ASK-080 enumerated surfaces). The burn-down map:
// Coordination's verify-all presence gate asserts each surface resolves to a plan here.
export const LOADING_SURFACES = Object.freeze({
	"scene-load":        "progress-staged",   // GLTFLoader onProgress — the headline fix
	"teleport":          "progress-staged",
	"shop-entry":        "progress",
	"product-inspect":   "skeleton",          // ASK-163 4s: the sheet's picture is a skeleton until it loads (imageSkeleton)
	"add-to-cart":       "optimistic",
	"checkout":          "inline",
	"publish-rebake":    "progress-staged",
	"directory-capture": "inline",
	"studio-compose":    "progress-staged",   // §2.18jr: the shell reports real bytes, then the fixtures are placed (studio-v1 viewport)
	"wizard-step":       "skeleton",
	"live-refresh":      "toast",             // the "Shop updated" non-intrusive notice
});

/** The indicator type for an enumerated surface (throws-free; unknown → "inline"). */
export function loadingPlan(surface) {
	return LOADING_SURFACES[surface] || "inline";
}

/** Overall 0..1 fraction + current label for a staged progress bar. Pure. */
export function stagedFraction(stages, idx, fracInStage = 0) {
	const n = Array.isArray(stages) ? stages.length : 0;
	if (n <= 0) return { fraction: 0, label: "" };
	const i = Math.max(0, Math.min(idx, n - 1));
	const f = Math.max(0, Math.min(fracInStage, 1));
	return { fraction: Math.max(0, Math.min((i + f) / n, 1)), label: stages[i] };
}

/** The 300ms rule: only reveal a spinner once work has run past the delay (avoids flash). */
export function spinnerDue(elapsedMs, delayMs = SPINNER_DELAY_MS) {
	return Number.isFinite(elapsedMs) && elapsedMs >= delayMs;
}

/** ASK-159 §2.18jr (Client 5f item 4) — OPTIMISTIC: apply the result NOW, reconcile when the work answers. `apply()` runs
 *  at once (inside the 300 ms rule by construction); `settle(ok)` keeps it, or runs `revert()` on a failure. Settles once.
 *  Pure (no DOM); the add-to-cart door (CartService.addItem) is its first caller. */
export function optimistic({ apply, revert } = {}) {
	let state = "applied";
	try { apply?.(); } catch (err) { state = "failed"; throw err; }
	return {
		get state() { return state; },
		settle(ok) {
			if (state !== "applied") return state;
			state = ok ? "kept" : "reverted";
			if (!ok) revert?.();
			return state;
		},
	};
}

// ── DOM adapters (guarded — no-op when there's no document, e.g. node tests) ──
const hasDOM = () => typeof document !== "undefined";

/** Polite live-region announcement for assistive tech (a11y rule). */
export function announce(msg) {
	if (!hasDOM() || !msg) return;
	let live = document.getElementById("kapowx-loading-live");
	if (!live) {
		live = document.createElement("div");
		live.id = "kapowx-loading-live";
		live.setAttribute("aria-live", "polite");
		live.setAttribute("role", "status");
		live.style.cssText = "position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);";
		document.body.appendChild(live);
	}
	live.textContent = msg;
}

/** 🔴 ASK-139 §94.14-2 / card 123qy9rtktu — a wait that has ENDED leaves no words behind. The live region kept the
 *  bar's last label ("Loading shop…") for the life of the page, and the Report's overlay read (a role=status root)
 *  painted it top-left into every picture after the shop had loaded. Cleared only while it still holds `msg`, so a
 *  later announcement ("Shop updated") is never wiped by an older wait finishing. */
export function clearAnnouncement(msg) {
	if (!hasDOM()) return;
	const live = document.getElementById("kapowx-loading-live");
	if (live && (msg == null || live.textContent === msg)) live.textContent = "";
}

/** Inline spinner on an element — short blocking action. Returns { stop() }. */
export function inline(el) {
	if (!hasDOM() || !el) return { stop() {} };
	el.setAttribute("aria-busy", "true");
	el.classList.add("is-loading");
	return { stop() { el.removeAttribute("aria-busy"); el.classList.remove("is-loading"); } };
}

/** Staged progress bar in `el`. Returns { set(idx,frac), done() }. Reveals respecting the 300ms rule. */
export function progress(el, { stages = [], label } = {}) {
	if (!hasDOM() || !el) return { set() {}, done() {} };
	const start = (typeof performance !== "undefined" ? performance.now() : 0);
	const bar = document.createElement("div");
	bar.className = "kapowx-loading-progress";
	bar.setAttribute("role", "progressbar");
	bar.setAttribute("aria-valuemin", "0");
	bar.setAttribute("aria-valuemax", "1");
	// ASK-135: drawn from the token sheet (`.kapowx-loading-progress`), not from
	// an inline palette — the skeleton is `--gg-skeleton` and the fill is the
	// accent, the same "this is working" colour every tool uses.
	bar.innerHTML = `<div class="kapowx-loading-progress__track"><div class="kapowx-loading-progress__fill"></div></div><span class="kapowx-loading-progress__label"></span>`;
	bar.hidden = true;
	el.appendChild(bar);
	const fill = bar.querySelector(".kapowx-loading-progress__fill");
	const lab = bar.querySelector(".kapowx-loading-progress__label");
	if (label) lab.textContent = label;
	let said = null;
	return {
		set(idx, frac) {
			const { fraction, label: l } = stagedFraction(stages, idx, frac);
			const now = (typeof performance !== "undefined" ? performance.now() : start);
			if (spinnerDue(now - start) || fraction > 0.05) bar.hidden = false;   // 300ms rule
			if (fill) fill.style.width = `${Math.round(fraction * 100)}%`;
			if (lab && l) lab.textContent = l;
			bar.setAttribute("aria-valuenow", String(fraction));
			said = l || label || "Loading";
			announce(said);
		},
		done() { bar.remove(); if (said) clearAnnouncement(said); },
	};
}

/** Skeleton placeholder of `rows` lines in `el`. Returns { done() }. */
export function skeleton(el, { rows = 3 } = {}) {
	if (!hasDOM() || !el) return { done() {} };
	const sk = document.createElement("div");
	sk.className = "kapowx-loading-skeleton";
	sk.setAttribute("aria-busy", "true");
	sk.innerHTML = Array.from({ length: rows }, () => `<div class="kapowx-loading-skeleton__row"></div>`).join("");
	el.appendChild(sk);
	return { done() { sk.remove(); } };
}

/** ASK-163 4s item 2 — is this <img> drawable whole? (complete AND decoded pixels; a broken image is not). Pure. */
export function imageReady(img) {
	return !!img && img.complete === true && (img.naturalWidth || 0) > 0;
}

/**
 * ASK-163 4s item 2 — the product sheet's picture (WoW §14.5 "product select/inspect"): a skeleton in the
 * media's shape until the image has loaded, never a half-painted progressive download (measured 2026-10-23,
 * coord client-4l `after-dev-5-sheet-comic1`: a fresh shop's cover drew only its top band). The image is
 * kept invisible (opacity, so it still downloads and lays out) until `load`; `error` ends the wait too and
 * calls `onError` so the caller can say "No image". A newer call on the same image supersedes an older one.
 * Returns { done() }.
 */
export function imageSkeleton(host, img, { onError } = {}) {
	if (!hasDOM() || !host || !img) return { done() {} };
	host.querySelector(":scope > .kapowx-loading-skeleton--media")?.remove();
	const token = (img.__kxSkeleton = (img.__kxSkeleton || 0) + 1);
	if (imageReady(img)) { img.style.opacity = ""; return { done() {} }; }
	const sk = document.createElement("div");
	sk.className = "kapowx-loading-skeleton kapowx-loading-skeleton--media";
	sk.setAttribute("aria-busy", "true");
	sk.setAttribute("aria-label", "Loading picture");
	if (getComputedStyle(host).position === "static") host.style.position = "relative";
	host.appendChild(sk);
	img.style.opacity = "0";
	const finish = (ok) => {
		img.removeEventListener("load", onLoad);
		img.removeEventListener("error", onErr);
		if (img.__kxSkeleton !== token) return;   // superseded by a newer picture
		sk.remove();
		img.style.opacity = "";
		if (!ok) onError?.();
	};
	const onLoad = () => finish(true);
	const onErr = () => finish(false);
	img.addEventListener("load", onLoad);
	img.addEventListener("error", onErr);
	return { done() { finish(true); } };
}
